/**
 * Accountant read-only financial surface (#135).
 *
 * Revenue / payouts / tips for a configurable date range + CSV export.
 * All endpoints are read-only; the accountant role has no write permission.
 * Server gates: revenue:read:all, payments:read:all, tips:read:all.
 */
import { useState, useEffect, useCallback } from 'react';
import type { ClientConfig } from '@ferro/config';
import { PageHeader } from '@ferro/ui';
import {
  accountantApi,
  humanizeApiError,
  type AccountantRevenueRow,
  type AccountantPayoutRow,
  type AccountantTipRow,
} from '../lib/api.js';
import { WelcomeCard } from '../components/WelcomeCard.js';

type AccountantHomeProps = { config: ClientConfig };

type Tab = 'revenue' | 'payouts' | 'tips';

function cents(n: number): string {
  return `$${(n / 100).toFixed(2)}`;
}

function isoToDisplay(iso: string): string {
  try {
    return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
}

function defaultRange(): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

export function AccountantHome({ config }: AccountantHomeProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const [tab, setTab] = useState<Tab>('revenue');
  const [range, setRange] = useState(defaultRange);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revenueRows, setRevenueRows] = useState<AccountantRevenueRow[]>([]);
  const [revTotal, setRevTotal] = useState(0);
  const [payouts, setPayouts] = useState<AccountantPayoutRow[]>([]);
  const [tips, setTips] = useState<AccountantTipRow[]>([]);
  const [tipsTotal, setTipsTotal] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const loadRevenue = useCallback(async () => {
    setIsLoading(true); setError(null);
    const res = await accountantApi.getRevenue(apiUrl, range.start, range.end);
    setIsLoading(false);
    if (res.error) { setError(humanizeApiError(res.error)); return; }
    if (res.data) { setRevenueRows(res.data.rows); setRevTotal(res.data.totalCents); }
  }, [apiUrl, range]);

  const loadPayouts = useCallback(async () => {
    setIsLoading(true); setError(null);
    const res = await accountantApi.getPayouts(apiUrl);
    setIsLoading(false);
    if (res.error) { setError(humanizeApiError(res.error)); return; }
    if (res.data) setPayouts(res.data.payouts);
  }, [apiUrl]);

  const loadTips = useCallback(async () => {
    setIsLoading(true); setError(null);
    const res = await accountantApi.getTips(apiUrl, range.start, range.end);
    setIsLoading(false);
    if (res.error) { setError(humanizeApiError(res.error)); return; }
    if (res.data) { setTips(res.data.tips); setTipsTotal(res.data.totalCents); }
  }, [apiUrl, range]);

  useEffect(() => {
    if (tab === 'revenue') void loadRevenue();
    else if (tab === 'payouts') void loadPayouts();
    else void loadTips();
  }, [tab, loadRevenue, loadPayouts, loadTips]);

  const handleExport = async (): Promise<void> => {
    setIsExporting(true);
    const res = await accountantApi.exportCsv(apiUrl, range.start, range.end);
    setIsExporting(false);
    if (res.error) { setError(humanizeApiError(res.error)); return; }
    if (res.data?.url) window.open(res.data.url, '_blank', 'noopener');
  };

  const statusStyle = (status: AccountantRevenueRow['status']): string =>
    status === 'settled' ? 'ferro-tx-table__settled' :
    status === 'refunded' ? 'ferro-tx-table__refunded' :
    'ferro-tx-table__void';

  return (
    <section className="ferro-admin-page ferro-role-screen">
      <PageHeader
        title="Financial Reports"
        subtitle="Read-only revenue &amp; payout overview"
        actions={
          <button
            type="button"
            className="ferro-btn ferro-btn--outline"
            onClick={() => void handleExport()}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </button>
        }
      />

      {/* #431 — first login only: the accountant blurb from their invite
          email, echoed on the screen the email pointed at. */}
      <WelcomeCard config={config} />

      {/* Period selector */}
      <div className="ferro-period-bar">
        <label htmlFor="acct-start">From</label>
        <input
          id="acct-start"
          type="date"
          value={range.start}
          max={range.end}
          onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
        />
        <label htmlFor="acct-end">To</label>
        <input
          id="acct-end"
          type="date"
          value={range.end}
          min={range.start}
          onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
        />
      </div>

      {/* Tabs */}
      <div className="ferro-tabs" role="tablist">
        {(['revenue', 'payouts', 'tips'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={`ferro-tabs__tab${tab === t ? ' ferro-tabs__tab--active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {error && (
        <div className="ferro-alert ferro-alert--error" role="alert">{error}</div>
      )}

      {isLoading && (
        <div className="ferro-skeleton-stack" data-testid="accountant-skeleton">
          {[1, 2, 3].map((i) => <div key={i} className="ferro-skeleton ferro-skeleton--stat" />)}
        </div>
      )}

      {!isLoading && !error && tab === 'revenue' && (
        <>
          <div className="ferro-summary-strip">
            <span className="ferro-summary-chip ferro-summary-chip--accent">
              Total: {cents(revTotal)}
            </span>
            <span className="ferro-summary-chip">{revenueRows.length} transactions</span>
          </div>
          {revenueRows.length === 0
            ? <div className="ferro-empty-card" data-testid="accountant-revenue-empty"><p>No transactions for this period.</p></div>
            : (
              <div className="ferro-table-card">
                <div className="ferro-table-scroll">
                  <table className="ferro-tx-table" aria-label="Revenue transactions">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Service</th>
                        <th>Method</th>
                        <th>Status</th>
                        <th className="ferro-tx-table__right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueRows.map((row, i) => (
                        <tr key={`${row.date}-${row.transactionRef ?? i}`}>
                          <td>{isoToDisplay(row.date)}</td>
                          <td>{row.serviceName}</td>
                          <td>{row.paymentMethod}</td>
                          <td><span className={statusStyle(row.status)}>{row.status}</span></td>
                          <td className="ferro-tx-table__right ferro-mono">{cents(row.totalCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          }
        </>
      )}

      {!isLoading && !error && tab === 'payouts' && (
        <>
          {payouts.length === 0
            ? <div className="ferro-empty-card" data-testid="accountant-payouts-empty"><p>No payout records available.</p></div>
            : (
              <div className="ferro-table-card">
                <div className="ferro-table-scroll">
                  <table className="ferro-tx-table" aria-label="Payout history">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Status</th>
                        <th>Paid At</th>
                        <th className="ferro-tx-table__right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map((p) => (
                        <tr key={p.id}>
                          <td>{isoToDisplay(p.periodStart)} — {isoToDisplay(p.periodEnd)}</td>
                          <td>
                            <span className={`ferro-status-pill ferro-status-pill--${p.status === 'paid' ? 'success' : p.status === 'failed' ? 'danger' : 'pending'}`}>
                              {p.status}
                            </span>
                          </td>
                          <td>{p.paidAt ? isoToDisplay(p.paidAt) : '—'}</td>
                          <td className="ferro-tx-table__right ferro-mono">{cents(p.totalCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          }
        </>
      )}

      {!isLoading && !error && tab === 'tips' && (
        <>
          <div className="ferro-summary-strip">
            <span className="ferro-summary-chip ferro-summary-chip--accent">Total tips: {cents(tipsTotal)}</span>
            <span className="ferro-summary-chip">{tips.length} entries</span>
          </div>
          {tips.length === 0
            ? <div className="ferro-empty-card" data-testid="accountant-tips-empty"><p>No tip records for this period.</p></div>
            : (
              <div className="ferro-table-card">
                <div className="ferro-table-scroll">
                  <table className="ferro-tx-table" aria-label="Tips breakdown">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Staff</th>
                        <th className="ferro-tx-table__right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tips.map((t, i) => (
                        <tr key={`${t.bookingId}-${i}`}>
                          <td>{isoToDisplay(t.date)}</td>
                          <td>{t.staffName}</td>
                          <td className="ferro-tx-table__right ferro-mono">{cents(t.amountCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          }
        </>
      )}
    </section>
  );
}
